// Shared mock OTP sessions (spec §9.3, §15): the code is always 123456,
// valid for 180 seconds of mock time, resend issues a fresh session.
import { loadDb, saveDb } from '../db';
import { mockNow } from '../clock';
import { fail, mockId, pause } from '../util';

const OTP_CODE = '123456';
const OTP_TTL_MS = 180_000;

export interface OtpSession {
  authId: string;
  code: string;
  expiresAt: number;
  failures: number;
  /** What the OTP is approving — payment sessions count failures towards the
   *  30-minute payments lock; beneficiary/profile sessions do not. */
  kind: 'PAYMENT' | 'BENEFICIARY' | 'PROFILE';
}

// OTP sessions are deliberately in-memory: they are short-lived and should not
// survive a page reload.
const sessions = new Map<string, OtpSession>();

export const startOtpSession = async (
  kind: OtpSession['kind']
): Promise<{ authId: string; expiresInSeconds: number }> => {
  await pause(300);
  const db = loadDb();
  const session: OtpSession = {
    authId: mockId('otp'),
    code: OTP_CODE,
    expiresAt: mockNow(db) + OTP_TTL_MS,
    failures: 0,
    kind,
  };
  sessions.set(session.authId, session);
  return { authId: session.authId, expiresInSeconds: OTP_TTL_MS / 1000 };
};

export const resendOtpSession = async (
  authId: string
): Promise<{ expiresInSeconds: number }> => {
  await pause(300);
  const db = loadDb();
  const session = sessions.get(authId) ?? fail('AUTH_EXPIRED', 'The OTP has expired. Please request a new OTP.');
  session.code = OTP_CODE;
  session.expiresAt = mockNow(db) + OTP_TTL_MS;
  session.failures = 0;
  return { expiresInSeconds: OTP_TTL_MS / 1000 };
};

/**
 * Verifies an OTP. On a payment session the third wrong code locks payments
 * for 30 minutes (spec §9.3). Expired codes get their own message.
 */
export const verifyOtpSession = async (authId: string, code: string): Promise<void> => {
  await pause(300);
  const db = loadDb();
  const session = sessions.get(authId) ?? fail('AUTH_EXPIRED', 'The OTP has expired. Please request a new OTP.');

  if (mockNow(db) > session.expiresAt) {
    sessions.delete(authId);
    fail('AUTH_EXPIRED', 'The OTP has expired. Please request a new OTP.');
  }
  if (code !== session.code) {
    session.failures += 1;
    if (session.kind === 'PAYMENT' && session.failures >= 3) {
      sessions.delete(authId);
      db.limits.paymentsLockedUntil = mockNow(db) + 30 * 60_000;
      saveDb(db);
      fail('PAYMENTS_LOCKED', 'Too many attempts. Payments are locked for 30 minutes.');
    }
    fail(
      'OTP_INCORRECT',
      'The One Time password (OTP) entered by you is Incorrect, hence your transaction cannot be processed.'
    );
  }
  sessions.delete(authId);
};

/** Test-panel helper: clearing lockouts when "Remove time limits" switches on. */
export const clearOtpSessions = (): void => sessions.clear();
